/**
 * WidgetHost — mounts ONE widget module instance (mirrors AppHost).
 * Dynamically imports the built /modules/<id>/index.js and calls its
 * mountWidget(el, ctx). ctx.widget is populated by createModuleContext
 * via the { surface:"widget", widget:{...} } opts.
 *
 * Uses the shared importModuleEsm loader (same as AppHost) so widget
 * modules resolve identically. A failed / missing / empty widget always
 * renders a small "⚠ …" error tile — never a silent blank.
 */
import { useEffect, useRef } from "react";
import type { ModuleContext, ModuleManifest } from "@arclog/module-sdk";
import { createModuleContext } from "../os/api";
import { importModuleEsm } from "../os/load-module";
import { openWidgetSettingsDialog } from "../os/widget-settings-dialog";

type Props = {
  moduleId: string;
  /** Placement id from osStore (desktopItems/panelItems). Keys this tile's settings. */
  placementId: string;
  cols: number;
  rows: number;
  surface: "desktop" | "panel";
};

export function WidgetHost({ moduleId, placementId, cols, rows, surface }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    let alive = true;
    let unmount: undefined | ((el?: HTMLElement) => void | Promise<void>);
    const el = ref.current;
    if (!el) return;
    el.innerHTML = "";
    const resizeSubs = new Set<(s: { cols: number; rows: number }) => void>();
    // Subscriptions handed out by ctx.widget.settings.onChange. They live in
    // osStore.listeners, which outlives this mount, so the host disposes them
    // even when the module forgets to — same defence as `unmount` above.
    const cleanups: Array<() => void> = [];
    let disposed = false;
    const registerCleanup = (fn: () => void) => {
      // Registered after teardown (module still finishing an interrupted
      // mount) — dispose immediately rather than parking a dead callback.
      if (disposed) {
        try {
          fn();
        } catch {
          /* */
        }
        return;
      }
      cleanups.push(fn);
    };
    (async () => {
      try {
        const url = `/modules/${moduleId}/index.js?t=${Date.now()}`;
        const mod = await importModuleEsm(url);
        const api = (mod?.default ?? mod) as {
          mountWidget?: (el: HTMLElement, ctx: unknown) => void | Promise<void>;
          unmount?: (el?: HTMLElement) => void | Promise<void>;
          renderSettings?: (el: HTMLElement, ctx: never) => void | Promise<void>;
          manifest?: ModuleManifest;
        };
        if (!alive) return;
        if (typeof api?.mountWidget !== "function") {
          renderError(el, `widget ${moduleId}: no mountWidget()`);
          return;
        }
        // Register teardown BEFORE awaiting mountWidget: a partial mount that
        // registers timers/listeners must still be torn down if cleanup fires
        // mid-mount (innerHTML="" clears DOM, not JS timers/intervals).
        unmount = api.unmount;
        const ctx = createModuleContext({
          appId: moduleId,
          surface: "widget",
          widget: {
            size: { cols, rows },
            on: surface,
            placementId,
            // Schema comes from the module we just loaded, so it always matches
            // the build actually running — not whatever the package list cached.
            settings: api.manifest?.widget?.settings,
            onResize: (cb) => {
              resizeSubs.add(cb);
              return () => resizeSubs.delete(cb);
            },
            registerCleanup,
          },
        });
        await api.mountWidget(el, ctx);
        if (!alive) return;
        if (!el.childNodes.length) {
          renderError(el, `widget ${moduleId}: rendered nothing`);
          return;
        }
        // AFTER mountWidget: modules routinely clear the host element, which
        // would eat a cog appended before the mount.
        mountCog(el, api, ctx as unknown as ModuleContext, surface, placementId);
      } catch (err) {
        if (alive)
          renderError(
            el,
            `widget ${moduleId} failed: ${String((err as Error)?.message || err)}`
          );
      }
    })();
    return () => {
      alive = false;
      disposed = true;
      try {
        void unmount?.(el);
      } catch {
        /* */
      }
      // After unmount(), so a module that unsubscribes itself wins the race and
      // these are no-ops (dispose is idempotent). One throwing callback must not
      // strand the rest.
      for (const fn of cleanups.splice(0)) {
        try {
          fn();
        } catch {
          /* */
        }
      }
      if (el) el.innerHTML = "";
    };
  }, [moduleId, placementId, cols, rows, surface]);
  return <div ref={ref} className="os-widget-host" />;
}

/**
 * Host-drawn settings affordance. Drawn here rather than per-widget so every
 * widget that declares a schema (or a renderSettings escape hatch) gets it free
 * — and so the OS, not the module, owns what the dialog looks like.
 */
function mountCog(
  el: HTMLElement,
  api: {
    renderSettings?: (el: HTMLElement, ctx: never) => void | Promise<void>;
    manifest?: ModuleManifest;
  },
  ctx: ModuleContext,
  surface: "desktop" | "panel",
  placementId: string
) {
  const manifest = api.manifest;
  if (!manifest) return;
  const hasSchema = !!manifest.widget?.settings?.length;
  const hasCustom = typeof api.renderSettings === "function";
  if (!hasSchema && !hasCustom) return;

  const cog = document.createElement("button");
  cog.type = "button";
  cog.className = "os-widget-cog";
  cog.title = "Widget settings";
  cog.setAttribute("aria-label", "Widget settings");
  cog.textContent = "⚙";
  // MUST stop here: DesktopGrid starts a tile drag from pointerdown on the
  // tile, so without this the cog is undraggable-to-click.
  cog.addEventListener("pointerdown", (e) => e.stopPropagation());
  cog.addEventListener("click", (e) => {
    e.stopPropagation();
    e.preventDefault();
    openWidgetSettingsDialog({
      placementId,
      manifest,
      surface,
      renderSettings: api.renderSettings as
        | ((el: HTMLElement, ctx: ModuleContext) => void | Promise<void>)
        | undefined,
      ctx,
    });
  });
  el.appendChild(cog);
}

function renderError(el: HTMLElement, msg: string) {
  el.innerHTML = "";
  const d = document.createElement("div");
  d.className = "os-widget-error";
  d.textContent = "⚠ " + msg;
  el.appendChild(d);
  // eslint-disable-next-line no-console
  console.warn("[WidgetHost]", msg);
}
